import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { PasswordMeter } from 'password-meter';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Constants from '../../utils/Constants';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import Field from './Field';
import { onActionKey } from '../../utils';
import './_styles_/PasswordField.css';

PasswordField.propTypes = {
    disabled: PropTypes.bool,
    label: PropTypes.node.isRequired,
    icon: PropTypes.string,
    iconTooltip: PropTypes.string,
    iconMiddlewares: PropTypes.arrayOf(PropTypes.shape({ offset: PropTypes.shape({ mainAxis: PropTypes.number, crossAxis: PropTypes.number }) })),
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    error: PropTypes.string,
    inputId: PropTypes.string,
    onChange: PropTypes.func,
    validator: PropTypes.func,
    onValidated: PropTypes.func,
    autoComplete: PropTypes.string,
    className: PropTypes.string,
    postfix: PropTypes.node,
    placeholder: PropTypes.string,
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    isRequired: PropTypes.bool,
    showPasswordStrength: PropTypes.bool,
};

export const getPasswordMeterResult = (value) => (
    new PasswordMeter({}, {
        40: 'very-weak',
        80: 'weak',
        120: 'medium',
        180: 'strong',
        200: 'very-strong',
        _: 'perfect',
    }).getResult(value)
);

function PasswordField(props) { // NOSONAR javascript:S3776
    const {
        disabled = false,
        label,
        icon = null,
        iconTooltip = null,
        iconMiddlewares,
        error = null,
        inputId = null,
        value = null,
        onChange = null,
        className = '',
        validator = null,
        onValidated,
        autoComplete = 'current-password',
        postfix = null,
        placeholder = '••••••••',
        onReset = null,
        isChanged = false,
        isRequired = false,
        showPasswordStrength = false,
    } = props;
    const { t } = useTranslation();
    const [isCapsLockOn, setCapsLockOn] = useState(false);
    const [currentDisplayValue, setCurrentDisplayValue] = useState(value);
    const [latestError, setLatestError] = useState(error);
    if (latestError === null && error !== null) {
        setLatestError(error); // NOSONAR javascript:S6442
    }
    const debouncedValidation = useDebouncedCallback((newValue) => {
        validator && validator(newValue)
            .then(() => onValidationComplete(null, newValue))
            .catch((e) => onValidationComplete(e, newValue));
    }, Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS);
    const onValidationComplete = (newError, newValue) => {
        onValidated && onValidated(newValue, validator === null ? null : !newError);
        setLatestError(newError);
    };
    const [passwordStrength, setPasswordStrength] = useState(getPasswordMeterResult(currentDisplayValue));
    const setNewValue = (newValue) => {
        setCurrentDisplayValue(newValue);
        if (showPasswordStrength) {
            setPasswordStrength(getPasswordMeterResult(newValue));
        }
        onChange && onChange(newValue);
        if (validator) {
            debouncedValidation(newValue);
        } else {
            onValidationComplete(null, newValue);
        }
    };
    useEffectOnUpdate(() => {
        if (value !== currentDisplayValue.trim()) {
            setNewValue(value);
        }
    }, [value]);
    const onChangeHandler = (event) => setNewValue(event.target.value);
    const capsLockSwitcherHandler = (event) => {
        if (event.key === 'CapsLock') {
            setCapsLockOn(!isCapsLockOn);
        }
    };
    const capsLockDetectHandler = (event) => setCapsLockOn(event.getModifierState('CapsLock'));
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const showPasswordHandler = () => setPasswordVisible(!isPasswordVisible);
    let showPasswordToggle;
    if (currentDisplayValue) {
        showPasswordToggle = (
            <div
                className={`show-password-toggle${isPasswordVisible ? ' password-visible' : ''}`}
                onClick={showPasswordHandler}
                onKeyUp={onActionKey(showPasswordHandler)}
                role="button"
                title={isPasswordVisible ? t('misc.hide-password') : t('misc.show-password')}
                tabIndex={-1}
            >
                {isPasswordVisible ? <FontAwesomeIcon icon="eye-slash" /> : <FontAwesomeIcon icon="eye" />}
            </div>
        );
    } else {
        showPasswordToggle = (
            <div
                className="show-password-toggle no-password"
                title={t('misc.no-password-entered')}
            >
                <FontAwesomeIcon icon="eye" />
            </div>
        );
    }
    return (
        <Field
            label={label}
            icon={icon}
            iconTooltip={iconTooltip}
            iconMiddlewares={iconMiddlewares}
            postfix={postfix}
            inputId={inputId}
            error={latestError}
            onReset={onReset}
            isChanged={isChanged}
            isRequired={isRequired && !currentDisplayValue.value}
        >
            <div
                className="password-field--container"
                title={showPasswordStrength ? t(`misc.password-meter.${passwordStrength.status.toLowerCase()}`) : null}
            >
                <div className="password-field--input-container">
                    <input
                        type={isPasswordVisible ? 'text' : 'password'}
                        id={inputId}
                        className={className}
                        value={currentDisplayValue || ''}
                        onChange={onChangeHandler}
                        autoComplete={autoComplete}
                        placeholder={placeholder}
                        aria-required={isRequired}
                        onKeyUp={capsLockSwitcherHandler}
                        onClick={capsLockDetectHandler}
                        disabled={disabled}
                    />
                    {showPasswordToggle}
                    {isCapsLockOn && (
                        <div className="caps-lock--on">{t('misc.caps-lock-on')}</div>
                    )}
                </div>
                {showPasswordStrength && (
                    <div className="password-field--password-strength-meter">
                        <div
                            className={`password-field--password-strength-meter--progress ${passwordStrength.status.toLowerCase()}`}
                            style={{ width: `${passwordStrength.percent}%` }}
                        />
                    </div>
                )}
            </div>
        </Field>
    );
}

export default memo(PasswordField);
